import { useState, useCallback, useRef, useEffect } from 'react';
import { User } from 'firebase/auth';
import {
  collection,
  query,
  orderBy,
  getDocs,
  doc,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { AgUiMessage, AgUiToolCall } from '../types/agui';

const generateThreadId = () =>
  'thread-' + Date.now().toString(36) + '-' + Math.random().toString(36).substring(2, 7);

import { getBackendHeaders, getEffectiveApiKey } from '../utils/backendHeaders';
export { isNonLocalhost, getBackendHeaders, getEffectiveApiKey, setBackendApiKey } from '../utils/backendHeaders';

export type SyncStatus = 'synced' | 'pending' | 'syncing' | 'error';

/**
 * Formats structured response from the Finance Advisor Agent into an institutional-grade Markdown report.
 */
export function formatFinanceAnalysis(structured: any, rawFallback?: string): string {
  if (!structured) return rawFallback || '';

  const { company, analysis, scenarios, evidence, disclaimer } = structured;
  const sections: string[] = [];

  // 1. Company Snapshot
  if (company) {
    const priceFormatted =
      company.currentPrice != null
        ? `${company.currentPrice.toLocaleString()} ${company.currency || 'INR'}`
        : 'N/A';
    const peFormatted =
      company.peRatio != null ? `${company.peRatio.toFixed(1)}x` : 'N/A';
    const mktCapFormatted =
      company.marketCap != null
        ? (company.marketCap >= 1e12
            ? `${(company.marketCap / 1e12).toFixed(2)}T`
            : company.marketCap >= 1e9
            ? `${(company.marketCap / 1e9).toFixed(2)}B`
            : company.marketCap.toLocaleString()) + ` ${company.currency || 'INR'}`
        : 'N/A';

    sections.push(`### 📊 ${company.companyName || company.ticker} (${company.ticker})
- **Asset Type:** ${company.assetType || 'EQUITY'}
- **Current Price:** **${priceFormatted}**
- **P/E Ratio:** ${peFormatted} | **Market Cap:** ${mktCapFormatted}
- **Data Source:** \`${company.source || 'ADK Gateway'}\` (${company.availability || 'AVAILABLE'})`);
  }

  // 2. Executive Analysis
  if (analysis) {
    let analysisBlock = `#### 📈 Executive Summary\n${analysis.summary || ''}`;
    if (analysis.keyFactors && analysis.keyFactors.length > 0) {
      analysisBlock +=
        `\n\n**Key Catalysts & Drivers:**\n` +
        analysis.keyFactors.map((k: string) => `- ${k}`).join('\n');
    }
    if (analysis.risks && analysis.risks.length > 0) {
      analysisBlock +=
        `\n\n**Risk Factors & Mitigations:**\n` +
        analysis.risks
          .map(
            (r: any) =>
              `- **[${r.severity || 'INFO'}] ${r.category || 'Risk'}:** ${r.description} *(Mitigation: ${r.mitigation})*`
          )
          .join('\n');
    }
    if (analysis.uncertainty) {
      analysisBlock += `\n\n*Uncertainty Assessment:* **${analysis.uncertainty}**`;
    }
    sections.push(analysisBlock);
  }

  // 3. Scenario Analysis
  if (scenarios) {
    let scenarioBlock = `#### 🎯 Multi-Scenario Projections\n`;
    const cases = [
      { key: 'baseline', label: 'Baseline Projection', icon: '🔹' },
      { key: 'upside', label: 'Upside Catalyst', icon: '🟢' },
      { key: 'stress', label: 'Stress Test (Downside)', icon: '🔴' },
    ];
    for (const c of cases) {
      const sc = scenarios[c.key];
      if (sc) {
        scenarioBlock +=
          `\n${c.icon} **${sc.name || c.label}**\n` +
          `- **Target Price:** **${sc.targetPrice != null ? sc.targetPrice + ' ' + (company?.currency || 'INR') : 'N/A'}**\n` +
          `- **Projection:** ${sc.projection}\n` +
          `- **Rationale:** ${sc.rationale}\n` +
          (sc.assumptions?.length
            ? `- **Assumptions:** ${sc.assumptions.join(', ')}\n`
            : '');
      }
    }
    sections.push(scenarioBlock);
  }

  // 4. Grounded Evidence
  if (evidence && evidence.length > 0) {
    const evidenceList = evidence
      .map(
        (e: any) =>
          `- **${e.sourceReference}** (${e.sourceType}): *${e.claim}*`
      )
      .join('\n');
    sections.push(`#### 🔍 Evidence & Data Grounding\n${evidenceList}`);
  }

  // 5. Regulatory Disclaimer
  if (disclaimer) {
    sections.push(`> ⚠️ **Regulatory Notice:** ${disclaimer}`);
  }

  return sections.length > 0 ? sections.join('\n\n---\n\n') : rawFallback || '';
}

interface UseAgUiChatOptions {
  currentUser?: User | null;
  initialAgentId?: string;
}

export function useAgUiChat(options?: UseAgUiChatOptions) {
  const currentUser = options?.currentUser;
  const initialAgentId = options?.initialAgentId;

  // Idle timeout in minutes from environment variable (default: 5 minutes)
  const idleMinutes = Math.max(
    1,
    parseInt(import.meta.env.VITE_CHAT_HISTORY_IDLE_MINUTES || '5', 10) || 5
  );

  const [threadId, setThreadId] = useState<string>(generateThreadId);
  const [messages, setMessages] = useState<AgUiMessage[]>([]);
  const [activeToolCalls, setActiveToolCalls] = useState<AgUiToolCall[]>([]);
  const [sessionState, setSessionState] = useState<Record<string, any>>({});
  const [isStreaming, setIsStreaming] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [syncStatus, setSyncStatus] = useState<SyncStatus>('synced');
  const [lastSyncedAt, setLastSyncedAt] = useState<Date | null>(null);

  const [targetAgentId, setTargetAgentId] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      const paramAgent = new URLSearchParams(window.location.search).get('agent');
      if (paramAgent) return paramAgent;
    }
    return initialAgentId || import.meta.env.VITE_TARGET_AGENT_ID || 'finance_advisor_v14';
  });

  // Track latest messages & unsaved status in refs to avoid stale closures in timeouts
  const messagesRef = useRef<AgUiMessage[]>(messages);
  const hasUnsavedChangesRef = useRef<boolean>(false);
  const idleTimerRef = useRef<NodeJS.Timeout | null>(null);
  const createdAtRef = useRef<string>(new Date().toISOString());

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // Flush in-memory messages to Cloud Firestore in a single atomic writeBatch
  const flushToFirestore = useCallback(async () => {
    if (!currentUser?.uid || !hasUnsavedChangesRef.current) return;
    const currentMessages = messagesRef.current;
    if (currentMessages.length === 0) return;

    setSyncStatus('syncing');
    try {
      const batch = writeBatch(db);
      const activeThreadId = threadId;

      // 1. Thread summary document
      const threadRef = doc(db, 'users', currentUser.uid, 'threads', activeThreadId);
      const firstUserMsg = currentMessages.find((m) => m.role === 'user');
      const title = firstUserMsg
        ? firstUserMsg.content.length > 42
          ? firstUserMsg.content.slice(0, 42) + '...'
          : firstUserMsg.content
        : 'Conversation';
      const lastMsg =
        currentMessages[currentMessages.length - 1]?.content.slice(0, 100) || '';

      batch.set(
        threadRef,
        {
          id: activeThreadId,
          userId: currentUser.uid,
          agentId: targetAgentId,
          title,
          lastMessage: lastMsg,
          messageCount: currentMessages.length,
          updatedAt: new Date().toISOString(),
          createdAt: createdAtRef.current,
        },
        { merge: true }
      );

      // 2. All message turn documents under the thread
      for (const msg of currentMessages) {
        const msgRef = doc(
          db,
          'users',
          currentUser.uid,
          'threads',
          activeThreadId,
          'messages',
          msg.id
        );
        batch.set(
          msgRef,
          {
            id: msg.id,
            threadId: activeThreadId,
            userId: currentUser.uid,
            role: msg.role,
            content: msg.content,
            timestamp: msg.timestamp || new Date().toLocaleTimeString(),
            createdAt: msg.createdAt || new Date().toISOString(),
          },
          { merge: true }
        );
      }

      await batch.commit();
      hasUnsavedChangesRef.current = false;
      setSyncStatus('synced');
      setLastSyncedAt(new Date());
      if (import.meta.env.DEV) {
        console.log(
          `[Firestore Sync] Flushed thread ${activeThreadId} (${currentMessages.length} messages) to Firestore in 1 atomic batch.`
        );
      }
    } catch (err: any) {
      console.error('[Firestore Sync] Failed to batch flush chat history:', err);
      setSyncStatus('error');
    }
  }, [currentUser, targetAgentId, threadId]);

  // Reset or start the idle timeout timer (5 minutes by default)
  const resetIdleTimer = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }
    hasUnsavedChangesRef.current = true;
    setSyncStatus('pending');

    idleTimerRef.current = setTimeout(() => {
      if (import.meta.env.DEV) {
        console.log(
          `[Firestore Sync] Session idle for ${idleMinutes} minutes. Triggering background Firestore sync...`
        );
      }
      flushToFirestore();
    }, idleMinutes * 60 * 1000);
  }, [flushToFirestore, idleMinutes]);

  // Background flush on page visibility change or tab close
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && hasUnsavedChangesRef.current) {
        flushToFirestore();
      }
    };

    window.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleVisibilityChange);

    return () => {
      window.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleVisibilityChange);
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }
    };
  }, [flushToFirestore]);

  // Session End Trigger 1: Clear / New Session
  const clearChat = useCallback(async () => {
    if (hasUnsavedChangesRef.current) {
      await flushToFirestore();
    }
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }
    setMessages([]);
    setActiveToolCalls([]);
    setSessionState({});
    setError(null);
    setSyncStatus('synced');
    hasUnsavedChangesRef.current = false;
    createdAtRef.current = new Date().toISOString();
    setThreadId(generateThreadId());
  }, [flushToFirestore]);

  // Session End Trigger 2: Switch to another existing thread
  const switchThread = useCallback(
    async (targetThreadId: string) => {
      if (!targetThreadId || targetThreadId === threadId) return;

      if (hasUnsavedChangesRef.current) {
        await flushToFirestore();
      }
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }

      setIsStreaming(false);
      setError(null);
      setActiveToolCalls([]);
      setSessionState({});
      setThreadId(targetThreadId);
      hasUnsavedChangesRef.current = false;
      setSyncStatus('synced');

      if (currentUser?.uid) {
        try {
          const msgsRef = collection(
            db,
            'users',
            currentUser.uid,
            'threads',
            targetThreadId,
            'messages'
          );
          const msgsQuery = query(msgsRef, orderBy('createdAt', 'asc'));
          const snapshot = await getDocs(msgsQuery);

          const loadedMessages: AgUiMessage[] = snapshot.docs.map((docSnap) => {
            const data = docSnap.data();
            return {
              id: docSnap.id,
              role: data.role || 'user',
              content: data.content || '',
              timestamp: data.timestamp || new Date().toLocaleTimeString(),
              toolCalls: data.toolCalls,
            };
          });

          setMessages(loadedMessages);
        } catch (err: any) {
          console.error('Failed to load thread messages from Firestore:', err);
          setError('Could not load chat messages for this thread.');
        }
      }
    },
    [currentUser, flushToFirestore, threadId]
  );

  const sendMessage = useCallback(
    async (userText: string) => {
      if (!userText.trim() || isStreaming) return;

      const userMsgId = 'msg-' + Math.random().toString(36).substring(2, 10);
      const userMessage: AgUiMessage = {
        id: userMsgId,
        role: 'user',
        content: userText,
        timestamp: new Date().toLocaleTimeString(),
        createdAt: new Date().toISOString(),
      };

      setMessages((prev) => [...prev, userMessage]);
      setIsStreaming(true);
      setError(null);

      // Mark session as having unsaved state and reset idle countdown (ZERO Firestore I/O)
      resetIdleTimer();

      const backendEndpoint =
        import.meta.env.VITE_BACKEND_AGENT_ENDPOINT || 'https://finadv-agent-api.adhyaconsulting.org';

      const callId = 'call-' + Date.now();
      setActiveToolCalls([
        {
          id: callId,
          name: `${targetAgentId || 'finance_advisor_v14'}/query`,
          status: 'calling',
        },
      ]);

      try {
        const restUrl = `${backendEndpoint.replace(/\/$/, '')}/api/v1/agents/${targetAgentId || 'finance_advisor_v14'}/query`;
        const restHeaders = getBackendHeaders(backendEndpoint, {
          'Content-Type': 'application/json',
        });
        const effectiveKey = getEffectiveApiKey();
        if (effectiveKey && !restHeaders['X-API-Key']) {
          restHeaders['X-API-Key'] = effectiveKey;
        }

        // Extract customer ID if present (e.g. "Customer ID 1002", "customer 1001", "ID 1002")
        const customerMatch = userText.match(/(?:customer(?:\s+id)?|\bid)\s*[:#]?\s*(\d{4,6})/i);
        const detectedCustomerId = customerMatch ? customerMatch[1] : undefined;

        // Extract stock ticker ONLY when not a stopword and not a customer/portfolio/profit query
        const STOPWORDS = new Set(['ID', 'AI', 'IT', 'US', 'UK', 'PE', 'EPS', 'DCF', 'INR', 'USD', 'BUY', 'SELL', 'FOR', 'AND', 'NOT', 'ALL', 'THE', 'P&L', 'PNL', 'SMA', 'CAGR', 'ROE', 'PROFIT', 'GAIN', 'LOSS', 'RETURN', 'MY']);
        let detectedTicker: string | undefined = undefined;
        if (!detectedCustomerId && !/\b(customer|portfolio|balance|holdings|invested|load|profit|pnl|gain|loss|return)\b/i.test(userText)) {
          const tickerMatch = userText.match(/\b([A-Z]{2,10}(?:\.[A-Z]{2,4})?)\b/);
          if (tickerMatch && !STOPWORDS.has(tickerMatch[1])) {
            detectedTicker = tickerMatch[1];
          }
        }

        const response = await fetch(restUrl, {
          method: 'POST',
          headers: restHeaders,
          body: JSON.stringify({
            query: userText,
            prompt: userText,
            customerId: detectedCustomerId || sessionState.customer_id,
            sessionId: threadId,
            parameters: {
              ...sessionState,
              ...(detectedCustomerId ? { customer_id: detectedCustomerId, customerId: detectedCustomerId } : {}),
              ...(detectedTicker ? { ticker: detectedTicker } : {}),
            },
          }),
        });

        if (!response.ok) {
          if (response.status === 401) {
            throw new Error(
              `Authentication required (401 Unauthorized) connecting to ${backendEndpoint}. Please click "Set Key" / "API Key" in the top navigation bar to configure your X-API-Key.`
            );
          }
          if (response.status === 403) {
            throw new Error(
              `Access forbidden (403) for agent '${targetAgentId}' at ${backendEndpoint}.`
            );
          }
          if (response.status === 502 || response.status === 504) {
            throw new Error(
              `Cannot connect to backend agent server (${backendEndpoint}). Please verify that the backend service is running and reachable.`
            );
          }
          throw new Error(`Agent backend error: HTTP ${response.status}`);
        }

        const data = await response.json();
        const formattedContent = formatFinanceAnalysis(data.structuredResponse, data.rawOutput);

        const assistantMsgId = 'msg-' + Math.random().toString(36).substring(2, 10);
        const assistantMsg: AgUiMessage = {
          id: assistantMsgId,
          role: 'assistant',
          content: formattedContent || 'Analysis completed with no content.',
          timestamp: new Date().toLocaleTimeString(),
          createdAt: new Date().toISOString(),
        };

        setMessages((prev) => [...prev, assistantMsg]);

        // Complete the active tool indicator with execution time
        setActiveToolCalls((prev) =>
          prev.map((t) =>
            t.id === callId
              ? {
                  ...t,
                  status: 'complete',
                  result: `Completed in ${data.executionTimeMs ?? 10}ms`,
                }
              : t
          )
        );

        // Update session state with company and metadata
        if (data.structuredResponse?.company) {
          setSessionState((prev) => ({
            ...prev,
            ticker: data.structuredResponse.company.ticker,
            currentPrice: data.structuredResponse.company.currentPrice,
            currency: data.structuredResponse.company.currency,
          }));
        }

        if (detectedCustomerId) {
          setSessionState((prev) => ({
            ...prev,
            customer_id: detectedCustomerId,
            customerId: detectedCustomerId,
          }));
        }

        // Assistant response ready: reset idle timer for background flush
        resetIdleTimer();
      } catch (err: any) {
        console.error('Agent communication failure:', err);
        const msg = err.message || '';
        if (
          msg === 'Failed to fetch' ||
          msg.includes('NetworkError') ||
          msg.includes('Load failed') ||
          msg.includes('ECONNREFUSED')
        ) {
          setError(
            `Cannot connect to backend agent server at ${backendEndpoint}. Please check that the server is online and CORS is configured for your domain.`
          );
        } else {
          setError(err.message || 'Unable to connect to backend agent server');
        }
      } finally {
        setIsStreaming(false);
      }
    },
    [isStreaming, resetIdleTimer, sessionState, targetAgentId, threadId]
  );

  return {
    messages,
    activeToolCalls,
    sessionState,
    isStreaming,
    error,
    threadId,
    targetAgentId,
    setTargetAgentId,
    sendMessage,
    clearChat,
    switchThread,
    syncStatus,
    lastSyncedAt,
    idleMinutes,
    syncNow: flushToFirestore,
  };
}
